import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { useDebouncedValue } from '../../shared/lib/use-debounced-value';
import { Button } from '../../shared/ui/button';
import { ChevronRightIcon, ClockIcon, SearchIcon } from '../../shared/ui/icons';
import { Input } from '../../shared/ui/input';
import { ErrorState, LoadingState } from '../../shared/ui/states';
import {
  inheritsWords,
  periodLabel,
  retentionExceptions,
  retentionKeys,
  warningText,
  type TFlowLevel,
} from '../../entities/retention';
import { FlowPeriod, followWords, useFlowPeriod } from './flow-period';

const PAGE = 20;

const Row = ({ flow }: { flow: TFlowLevel }) => {
  const follow = useFlowPeriod();
  return (
    <div className="border-t border-border-row px-card">
      <div className="flex min-h-row-xl items-center gap-3">
        <Link
          to="/flows/$flowId"
          params={{ flowId: flow.flowId }}
          className="min-w-0 flex-1 truncate text-data text-primary hover:underline"
        >
          {flow.name}
        </Link>
        <span className="w-col-inherits shrink-0 truncate text-data text-muted">
          {followWords(flow)}
        </span>
        <span className="flex w-col-arrow shrink-0 justify-center text-muted">
          <ChevronRightIcon size="sm" />
        </span>
        <span className="w-col-keeps shrink-0">
          <FlowPeriod flow={flow} />
        </span>
        <span className="flex w-col-undo shrink-0 justify-end">
          <Button
            variant="ghost"
            size="xs"
            waiting={follow.isPending}
            onClick={() => follow.mutate({ flowId: flow.flowId, period: null })}
          >
            Use {inheritsWords(flow.inherits) === 'the default' ? 'the default' : 'the folder’s'}{' '}
            {periodLabel(flow.inherits.period)}
          </Button>
        </span>
      </div>
      {flow.warning && (
        <p className="flex gap-label pb-inset text-meta leading-17 text-motion">
          <ClockIcon size="sm" className="mt-hair" />
          {warningText(flow.warning, flow.keeps.period)}
        </p>
      )}
    </div>
  );
};

export const ExceptionsList = ({ total }: { total: number }) => {
  const [q, setQ] = useState('');
  const [offset, setOffset] = useState(0);
  const needle = useDebouncedValue(q.trim(), 250);
  const list = useQuery({
    queryKey: retentionKeys.exceptions(needle, offset),
    queryFn: () => retentionExceptions({ q: needle, offset, limit: PAGE }),
    placeholderData: keepPreviousData,
  });

  if (total === 0) return null;
  const rows = list.data?.data ?? [];
  const matched = list.data?.total ?? 0;

  return (
    <div className="border-t border-border-sunken">
      <div className="flex items-center gap-inset px-card pt-4 pb-inset">
        <h3 className="text-data font-semibold text-primary">
          Flows that differ from their folder
        </h3>
        <span className="font-mono text-micro text-faint">{total.toLocaleString('en-US')}</span>
        {total > PAGE && (
          <Input
            size="sm"
            type="search"
            aria-label="Find an exception"
            placeholder="Find an exception"
            leading={<SearchIcon size="sm" />}
            value={q}
            onChange={(event) => {
              setQ(event.target.value);
              setOffset(0);
            }}
            className="ml-auto w-col-keeps"
          />
        )}
      </div>
      {list.isPending && <LoadingState rows={3} />}
      {list.isError && <ErrorState error={list.error} onRetry={() => void list.refetch()} />}
      {rows.map((flow) => (
        <Row key={flow.flowId} flow={flow} />
      ))}
      {list.isSuccess && needle && rows.length === 0 && (
        <p className="border-t border-border-row px-card py-3 text-meta text-muted">
          No exception is called anything like “{needle}”.
        </p>
      )}
      {matched > PAGE && (
        <div className="flex items-center gap-inset border-t border-border-row px-card py-3">
          <span className="font-mono text-micro text-faint">
            problems first · {offset + 1}–{Math.min(matched, offset + rows.length)} of{' '}
            {matched.toLocaleString('en-US')}
          </span>
          <span className="ml-auto flex gap-2">
            {offset > 0 && (
              <Button variant="ghost" size="xs" onClick={() => setOffset(offset - PAGE)}>
                Previous
              </Button>
            )}
            {offset + PAGE < matched && (
              <Button variant="ghost" size="xs" onClick={() => setOffset(offset + PAGE)}>
                Next {Math.min(PAGE, matched - offset - PAGE)}
              </Button>
            )}
          </span>
        </div>
      )}
    </div>
  );
};
